import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RangeSlider.css';

const RangeSlider = ({ min, max, value, onChange, step = 1, label, disabled = false }) => {
  const [localMin, setLocalMin] = useState(value.min);
  const [localMax, setLocalMax] = useState(value.max);
  const [inputMin, setInputMin] = useState(String(value.min));
  const [inputMax, setInputMax] = useState(String(value.max));
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    setLocalMin(value.min);
    setLocalMax(value.max);
    setInputMin(String(value.min));
    setInputMax(String(value.max));
  }, [value]);

  const handleMouseDown = (thumb) => {
    if (disabled) return;
    setIsDragging(thumb);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || disabled) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newValue = Math.round((percent * (max - min) + min) / step) * step;

    if (isDragging === 'min') {
      const newMin = Math.min(newValue, localMax - step);
      setLocalMin(newMin);
      setInputMin(String(newMin));
      onChange({ min: newMin, max: localMax });
    } else if (isDragging === 'max') {
      const newMax = Math.max(newValue, localMin + step);
      setLocalMax(newMax);
      setInputMax(String(newMax));
      onChange({ min: localMin, max: newMax });
    }
  }, [isDragging, disabled, max, min, step, localMax, localMin, onChange]);

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, localMin, localMax, handleMouseMove]);

  const handleInputChange = (type, inputValue) => {
    // Allow empty string and partial input while typing
    if (type === 'min') {
      setInputMin(inputValue);
    } else {
      setInputMax(inputValue);
    }
  };

  const handleInputBlur = (type) => {
    // Validate and clamp the value when user finishes typing
    if (type === 'min') {
      const numValue = parseInt(inputMin) || min;
      const newMin = Math.max(min, Math.min(numValue, localMax - step));
      setLocalMin(newMin);
      setInputMin(String(newMin));
      onChange({ min: newMin, max: localMax });
    } else {
      const numValue = parseInt(inputMax) || max;
      const newMax = Math.min(max, Math.max(numValue, localMin + step));
      setLocalMax(newMax);
      setInputMax(String(newMax));
      onChange({ min: localMin, max: newMax });
    }
  };

  const handleInputKeyDown = (e, type) => {
    // Handle Enter key to validate immediately
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const minPercent = ((localMin - min) / (max - min)) * 100;
  const maxPercent = ((localMax - min) / (max - min)) * 100;

  return (
    <div className={`range-slider ${disabled ? 'disabled' : ''}`}>
      <div className="range-inputs">
        <div className="input-group">
          <label>Min</label>
          <input
            type="number"
            value={inputMin}
            onChange={(e) => handleInputChange('min', e.target.value)}
            onBlur={() => handleInputBlur('min')}
            onKeyDown={(e) => handleInputKeyDown(e, 'min')}
            min={min}
            max={max}
            disabled={disabled}
            className="range-input"
          />
        </div>
        <div className="input-group">
          <label>Max</label>
          <input
            type="number"
            value={inputMax}
            onChange={(e) => handleInputChange('max', e.target.value)}
            onBlur={() => handleInputBlur('max')}
            onKeyDown={(e) => handleInputKeyDown(e, 'max')}
            min={min}
            max={max}
            disabled={disabled}
            className="range-input"
          />
        </div>
      </div>
      
      <div className="slider-container" ref={sliderRef}>
        <div className="slider-track"></div>
        <div 
          className="slider-range"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`
          }}
        ></div>
        <div
          className="slider-thumb slider-thumb-min"
          style={{ left: `${minPercent}%` }}
          onMouseDown={() => handleMouseDown('min')}
        ></div>
        <div
          className="slider-thumb slider-thumb-max"
          style={{ left: `${maxPercent}%` }}
          onMouseDown={() => handleMouseDown('max')}
        ></div>
      </div>
      
      <div className="range-labels">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
};

export default RangeSlider;